import dayjs from 'dayjs'
import { ReactElement } from 'react'
import {
  Alert,
  Box,
  Checkbox,
  Divider,
  FormControlLabel,
  TextField,
  Typography,
} from '@mui/material'
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { WarningAmberOutlined } from '@mui/icons-material'
import { paperInputSx } from '../../styles'
import RecurrenceFields from './recurrence-fields'
import { calculateDueDaysAfterGoLive } from '../../utils/date-utils'

type SchedulingFieldsGroupProps = {
  form: any
  isLoadingCourses?: boolean
  hasAnyProgress?: boolean
  linkedServiceID?: string
}

export default function SchedulingFieldsGroup({
  form,
}: SchedulingFieldsGroupProps): ReactElement {
  let handleRecurrenceToggle = (
    checked: boolean,
    goLiveDate: string,
    setRecurrence: (val: any) => void,
    setHasRecurrence: (val: boolean) => void,
  ) => {
    setHasRecurrence(checked)
    if (!checked) {
      setRecurrence(undefined)
    } else {
      setRecurrence({
        recurrenceType: 'Monthly',
        recurrenceInterval: 1,
        recurrenceEndDate: '',
        dayOfMonth: goLiveDate ? dayjs(goLiveDate).date() : 1,
      })
    }
  }

  return (
    <Box>
      <Typography variant="h6" component="h3" className="mb-3">
        Scheduling Details
      </Typography>
      <Alert severity="warning" icon={<WarningAmberOutlined />} sx={{ mb: 3 }}>
        <Typography variant="body2" sx={{ mb: 1 }}>
          <strong>Important:</strong> Creating a scheduled assignment will queue
          it to be created automatically on the go-live date. Any users who need
          to be added between now and that date must be added manually by
          visiting the <strong>Scheduled Assignments</strong> page and editing
          the queued schedule.
        </Typography>
        <Typography variant="body2" sx={{ mb: 0.5 }}>
          <strong>Assignment Thresholds:</strong>
        </Typography>
        <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
          <Box component="li" sx={{ mb: 0.5 }}>
            <Typography variant="body2">
              If assignment thresholds are enabled and your Linked Service is
              configured to apply them to scheduled assignments, users who have
              already completed the course within the threshold window will
              automatically receive credit.
            </Typography>
          </Box>
          <Box component="li">
            <Typography variant="body2">
              Whether thresholds are applied depends on whether the setting is
              toggled on for your Linked Service at the time the assignment is
              automatically created. Go to{' '}
              <strong>
                Manage &rarr; Linked Service &rarr; Assignment Thresholds
              </strong>{' '}
              to configure this.
            </Typography>
          </Box>
        </Box>
      </Alert>
      <Box className="grid grid-cols-1 gap-4 md:grid-cols-3 mb-4">
        <form.Field
          name="name"
          validators={{
            onChange: ({ value }: { value: string }) =>
              !value ? 'Assignment name is required' : undefined,
          }}
        >
          {(field: any) => (
            <TextField
              label="Assignment Name"
              value={field.state.value}
              onChange={(e) => field.handleChange(e.target.value)}
              onBlur={field.handleBlur}
              error={!field.state.meta.isValid}
              helperText={field.state.meta.errors.join(', ')}
              fullWidth
              required
              sx={paperInputSx}
            />
          )}
        </form.Field>

        <form.Field
          name="goLiveDate"
          validators={{
            onChange: ({ value }: { value: string }) => {
              if (!value) return 'Go live date is required'
              if (!dayjs(value, undefined, true).isValid())
                return 'Go live date must be a valid date'
              if (!dayjs(value).isAfter(dayjs(), 'day'))
                return 'Go live date must be in the future'
              return undefined
            },
          }}
        >
          {(field: any) => (
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="Go Live Date"
                value={field.state.value ? dayjs(field.state.value) : null}
                onChange={(date) =>
                  field.handleChange(date?.toISOString() || '')
                }
                minDate={dayjs().add(1, 'day')}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    required: true,
                    error: !field.state.meta.isValid,
                    helperText: field.state.meta.errors.join(', '),
                    onBlur: field.handleBlur,

                    slotProps: {
                      input: {
                        sx: { bgcolor: 'background.paper' },
                      },
                    },
                  },
                }}
              />
            </LocalizationProvider>
          )}
        </form.Field>

        <form.Subscribe
          selector={(state: any) => [
            state.values.goLiveDate,
            state.values.dueDate,
          ]}
        >
          {([goLiveDate, dueDate]: [string, string]) => {
            let dueDaysAfterGoLive =
              goLiveDate && dueDate
                ? calculateDueDaysAfterGoLive(goLiveDate, dueDate)
                : 0

            return (
              <form.Field
                name="dueDate"
                validators={{
                  onChange: ({ value }: { value: string }) => {
                    if (!value) return 'Due date is required'
                    if (!dayjs(value, undefined, true).isValid())
                      return 'Due date must be a valid date'
                    return undefined
                  },
                }}
              >
                {(field: any) => (
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      label="Due Date"
                      value={
                        field.state.value ? dayjs(field.state.value) : null
                      }
                      onChange={(date) =>
                        field.handleChange(date?.toISOString() || '')
                      }
                      minDate={
                        goLiveDate ? dayjs(goLiveDate) : dayjs().add(1, 'day')
                      }
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          required: true,
                          error: !field.state.meta.isValid,

                          helperText:
                            field.state.meta.errors.join(', ') ||
                            (dueDaysAfterGoLive > 0
                              ? `${dueDaysAfterGoLive} days after go-live`
                              : ''),

                          onBlur: field.handleBlur,

                          slotProps: {
                            input: {
                              sx: { bgcolor: 'background.paper' },
                            },
                          },
                        },
                      }}
                    />
                  </LocalizationProvider>
                )}
              </form.Field>
            )
          }}
        </form.Subscribe>
      </Box>
      <Divider sx={{ my: 3 }} />
      <form.Subscribe
        selector={(state: any) => [
          state.values.hasRecurrence,
          state.values.goLiveDate,
        ]}
      >
        {([hasRecurrence, goLiveDate]: [boolean, string]) => (
          <>
            <form.Field name="hasRecurrence">
              {(hasRecurrenceField: any) => (
                <form.Field name="recurrence">
                  {(recurrenceField: any) => (
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={hasRecurrence || false}
                          onChange={(e) =>
                            handleRecurrenceToggle(
                              e.target.checked,
                              goLiveDate || '',
                              recurrenceField.handleChange,
                              hasRecurrenceField.handleChange,
                            )
                          }
                        />
                      }
                      label={
                        <Box>
                          <Typography variant="body1">
                            Automatically repeat at regular intervals
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Generate new assignments on a recurring basis
                            (monthly or yearly)
                          </Typography>
                        </Box>
                      }
                    />
                  )}
                </form.Field>
              )}
            </form.Field>

            {hasRecurrence && (
              <Box sx={{ mt: 3, pl: 4 }}>
                <form.Field name="recurrence">
                  {(field: any) => (
                    <form.Field name="goLiveDate">
                      {(goLiveDateField: any) => (
                        <RecurrenceFields
                          recurrence={field.state.value}
                          goLiveDate={goLiveDateField.state.value || ''}
                          onRecurrenceChange={field.handleChange}
                          errors={{}}
                        />
                      )}
                    </form.Field>
                  )}
                </form.Field>
              </Box>
            )}
          </>
        )}
      </form.Subscribe>
    </Box>
  )
}
